import noUiSlider from 'nouislider';

/**
 * Custom element for managing a price range slider filter.
 * Extends HTMLElement to create a web component that integrates with noUiSlider
 * to provide a dual-handle price range selector.
 *
 * @class PriceRange
 * @extends {HTMLElement}
 */
class PriceRange extends HTMLElement {
  /** @type {HTMLWebComponentElement} The filter form web component element */
  form = this.closest('code-filters-form');

  /** @type {HTMLInputElement} The input element for the minimum price value */
  minInput = this.querySelector('[data-min]');

  /** @type {HTMLInputElement} The input element for the maximum price value */
  maxInput = this.querySelector('[data-max]');

  /** @type {HTMLInputElement} The hidden input element for the custom price filter */
  hiddenInput = this.querySelector('[data-custom-price-filter]');

  /** @type {HTMLElement} The container element where the noUiSlider will be rendered */
  priceSlider = this.querySelector('[data-price-slider]');

  /** @type {noUiSlider} The noUiSlider instance, null until createSlider is called */
  slider = null;

  /** @type {Array<number>} The prices data attribute */
  prices = Array.from(this.getAttribute('data-prices')?.split(',') || '').map(Number);

  /**
   * Lifecycle callback called when the element is inserted into the DOM.
   * Initializes the price range slider if the required elements are present.
   *
   * @memberof PriceRange
   */
  connectedCallback() {
    const params =
      this.getAttribute('data-params') && this.getAttribute('data-params') !== 'undefined'
        ? JSON.parse(this.getAttribute('data-params'))
        : new URLSearchParams(window.location.search);
    if (params) {
      this.updateValuesFromURL(params);
    }

    this.createSlider();
  }

  /**
   * Creates and initializes the noUiSlider instance with configuration
   * based on the min and max input values.
   * Sets up the slider range, formatting, and event handlers.
   *
   * @memberof PriceRange
   */
  createSlider() {
    this.slider = noUiSlider.create(this.priceSlider, {
      start: [Number(this.minInput.value), Number(this.maxInput.value)],
      connect: true,
      step: 1,
      tooltips: false,
      range: {
        min: Number(this.minInput.min),
        max: Number(this.maxInput.max),
      },
      format: {
        to: (value) => Number(value).toFixed(0),
        from: (value) => Number(value).toFixed(0),
      },
    });

    this.registerEvents();
  }

  /**
   * Registers event listeners for slider interactions.
   *
   * @memberof PriceRange
   */
  registerEvents() {
    this.slider.on('slide', this.handleSlide.bind(this));
    this.slider.on('change', this.handleChange.bind(this));
  }

  /**
   * Handles the slide event fired while the user is dragging the slider handles.
   * Updates the corresponding input field value in real-time as the user drags.
   *
   * @param {number[]} values - Array containing the current min and max slider values
   * @param {number} handle - Index of the handle being moved (0 for min, 1 for max)
   * @memberof PriceRange
   */
  handleSlide(values, handle) {
    const inputs = [this.minInput, this.maxInput];
    const changedInput = inputs[handle];
    changedInput.value = values[handle];
  }

  /**
   * Handles the change event fired when the range values are changed.
   * Updates the hidden input value and submits the form if the prices are different.
   *
   * @param {number[]} values - The current range values
   * @returns {void}
   * @memberof PriceRange
   */
  handleChange([min, max]) {
    if (this.prices.length > 0) {
      const newPrices = this.prices.filter((price) => price >= min && price <= max);

      if (PriceRange.areArraysEqual(newPrices, this.prices)) {
        return;
      }

      this.hiddenInput.value = newPrices.length > 0 ? newPrices.join(',') : false;
    }

    this.form?.handleFormInput?.();
  }

  /**
   * Updates the min and max input values from the URL search parameters.
   *
   * @param {Object} params - The URL search parameters
   * @returns {void}
   * @memberof PriceRange
   */
  updateValuesFromURL(params) {
    const windowParams = new URLSearchParams(window.location.search);
    const min = params[this.minInput.name] || windowParams.get(this.minInput.name);
    const max = params[this.maxInput.name] || windowParams.get(this.maxInput.name);

    if (min) {
      this.minInput.value = min;
      this.slider?.set([min, null]);
    }
    if (max) {
      this.maxInput.value = max;
      this.slider?.set([null, max]);
    }
  }

  /**
   * Checks if two arrays are equal.
   *
   * @param {Array<number>} arr1 - The first array to compare
   * @param {Array<number>} arr2 - The second array to compare
   * @returns {boolean} True if the arrays are equal, false otherwise
   */
  static areArraysEqual = (arr1, arr2) => {
    const set = new Set(arr1);
    return set.size === arr2.length && arr2.every((value) => set.has(value));
  };
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}
